/**
 * Набор функций для простого отслеживания активности пользователя
 * Активностью считаются действия пользователя на странице (движение мышкой, скролл, клик)
 *
 * @see onActivityStart
 * @see onActivityStop
 *
 * @author Aleksandr Bavin
 * @date 10.06.2022
 */

import throttle from "OK/utils/throttle";
import * as userActivityConfig from 'OK/pms!userActivityConfig';
import {isDebug} from "OK/utils/environment";

interface IConfig {
    config?: {
        activityStartDelay: number; // время действий пользователя, что бы считать его активным
        activityStartCancelDelay: number; // время бездействия для сброса начала активности
        activityStopDelay: number; // время бездействия пользователя после начала активности, что бы считать его неактивным
        debug: boolean;
    }
}

const {config} = userActivityConfig as IConfig;

interface IActivityStopListener {
    (activeTime: number): void;
}

interface IActivityStartListener {
    (idleTime: number): void;
}

const activityStartDelay = config?.activityStartDelay || 5000;
const activityStartCancelDelay = config?.activityStartCancelDelay || 3000;
const activityStopDelay = config?.activityStopDelay || 10000;
const throttleDelay = 100;
const activityStopListeners: IActivityStopListener[] = [];
const activityStartListeners: IActivityStartListener[] = [];
let activityStartTimer: number | null = null;
let activityStartDisableTimer: number | null = null;
let activityStopTimer: number | null = null;
let active = false;
let activityStartTimestamp = Date.now();
let activityStopTimestamp = activityStartTimestamp;
let isListeningActivity = false;

function consoleDebugLog(...data: any[]) {
    if (!isDebug || !config || !config.debug) {
        return;
    }

    console.log('UserActivity:', ...data);
}

// инициализация обработчиков - один раз
function initActivityListener() {
    if (!isListeningActivity && config) {
        isListeningActivity = true;
        activityStartTimestamp = Date.now();
        activityStopTimestamp = activityStartTimestamp;
        document.addEventListener('mousemove', onActivityCallback);
        document.addEventListener('click', onActivityCallback);
        window.addEventListener('scroll', onActivityCallback);
    }
}

// основной обработчик действий пользователя
const onActivityCallback = throttle(throttleDelay, function () {
    // откладываем остановку активности
    clearTimeout(activityStopTimer!);
    clearTimeout(activityStartDisableTimer!);
    activityStopTimer = setTimeout(stopActivity, activityStopDelay);

    // пробуем отменить начало активности, если еще не начата
    if (!active) {
        activityStartDisableTimer = setTimeout(stopActivity, activityStartCancelDelay);
    }

    if (activityStartTimer === null) {
        activityStartTimer = setTimeout(startActivity, activityStartDelay);
    }
});

// окончание активности пользователя
function stopActivity() {
    clearTimeout(activityStopTimer!);
    clearTimeout(activityStartTimer!);
    clearTimeout(activityStartDisableTimer!);
    activityStartTimer = null;

    if (active) {
        active = false;
        activityStopTimestamp = Date.now();
        const activeTime = activityStopTimestamp - activityStartTimestamp;

        consoleDebugLog('stopActivity - activeTime', activeTime);

        // оповещаем подписчиков
        for (let i = 0, ln = activityStopListeners.length; i < ln; i++) {
            activityStopListeners[i](activeTime);
        }
    }

}

// начало кативности пользователя
function startActivity() {
    clearTimeout(activityStopTimer!);
    clearTimeout(activityStartDisableTimer!);
    activityStopTimer = setTimeout(stopActivity, activityStopDelay);

    if (!active) {
        active = true;
        activityStartTimestamp = Date.now();
        const idleTime = activityStartTimestamp - activityStopTimestamp;

        consoleDebugLog('startActivity - idleTime', idleTime);

        // оповещаем подписчиков
        for (let i = 0, ln = activityStartListeners.length; i < ln; i++) {
            activityStartListeners[i](idleTime);
        }
    }
}

// добавляет обработчик в массив и возвращает функцию для отписки
function createDestructor(key: string, listener: (...args: any[]) => void, array: any[]): () => void {
    array.push(listener);

    return destroyListener.bind(null, listener, array);
}

// отписка обработчика
function destroyListener(listener: (...args: any[]) => void, array: any[]) {
    const index = array.indexOf(listener);

    if (index !== -1) {
        array.splice(index, 1);
    }
}

// подписка на начало активности пользоателя
export function onActivityStart(key: string, listener: IActivityStartListener) {
    initActivityListener();
    return createDestructor(key, listener, activityStartListeners);
}

// подписка на окончание активности пользователя
export function onActivityStop(key: string, listener: IActivityStopListener) {
    initActivityListener();
    return createDestructor(key, listener, activityStopListeners);
}
